import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import { GENERATOR_INPUT_TYPE } from '@constants'
import { SelectOption } from '@types'

import { AXIS_TYPE, TimelineItem } from '@gantt/components/GanttCreateOrEdit'
import { getRadioOptionsByEnum } from '@gantt/helpers'

import { SortFieldArray } from './components/SortFieldArray'
import { useDefaultSortForm } from './hooks/useDefaultSortForm'

export const DefaultSortForm = () => {
  const { watch, setValue } = useFormContext()

  const { t } = useTranslation()
  const watchAxis = watch('sort.axis')
  const watchBarList = watch('timeline') as TimelineItem[]
  const watchedObjectY = watch('resource.data')
  // TODO: в дальнейшем избавиться от варианта с бесконечным скроллом.
  //  у нас всегда будет isTimeWindow === true
  const watchIsTimeWindow = true
  const watchedSortObjectCode = watch('sort.code')
  const [watchedObject, setWatchedObject] = useState(watchedObjectY?.id)
  const { objectForField, isLoading } = useDefaultSortForm({
    objectCode: watchedObject,
  })
  const [mainObjectOptions, setMainObjectOptions] = useState<SelectOption[]>([])

  const mainObjectOptionsY = useMemo(() => {
    if (watchedObjectY) {
      return [
        {
          id: watchedObjectY?.label,
          name: watchedObjectY?.label,
          value: watchedObjectY?.id,
        },
      ]
    }

    return []
  }, [watchedObjectY])

  const mainObjectOptionsX = useMemo(() => {
    return watchBarList
      ?.filter(item => item.data)
      .map((item, index) => ({
        id: t('ganttCreate.timelineForm.barName', { index, name: item.data?.label }),
        name: t('ganttCreate.timelineForm.barName', { index: index + 1, name: item.data?.label }),
        value: item.data?.id,
      }))
  }, [watchBarList])

  const handleSetMainObjectOptions = (axis: string) => {
    const isSelectedObjectsX = Boolean(watchBarList?.filter(item => item.data).length)

    if (axis === AXIS_TYPE.Y && watchedObjectY) {
      return setMainObjectOptions(mainObjectOptionsY)
    }

    if (axis === AXIS_TYPE.X && isSelectedObjectsX) {
      return setMainObjectOptions(mainObjectOptionsX)
    }

    return setMainObjectOptions([])
  }

  const fieldsSelectOptions = useMemo(() => {
    if (!watchedObject) {
      return []
    }

    return objectForField?.fields
  }, [objectForField, watchAxis, watchedObject])

  const axisOptions = useMemo(() => getRadioOptionsByEnum(AXIS_TYPE), [])

  const handleInit = (axis: AXIS_TYPE) => {
    if (!watchIsTimeWindow) {
      setValue('sort.axis', AXIS_TYPE.Y)
      handleChangeSort(AXIS_TYPE.Y)

      return
    }

    handleSetMainObjectOptions(axis)

    if (axis === AXIS_TYPE.Y && !watchedSortObjectCode) {
      setValue('sort.code', watchedObjectY?.id)
      setWatchedObject(watchedObjectY?.id)
    }

    if (axis === AXIS_TYPE.X) {
      setValue('sort.code', watchBarList.at(0)?.data?.id)
      setWatchedObject(watchBarList.at(0)?.data?.id)
    }
  }

  useEffect(() => {
    handleInit(watchAxis)
  }, [])

  const handleChangeSortObject = (value?: string | number) => {
    setWatchedObject(value)
    handleClearValue()
  }

  const handleClearValue = () => {
    setValue('sort.value', [{ field: null, order: null }])
  }

  const handleChangeSort = (value: string) => {
    handleClearValue()
    handleSetMainObjectOptions(value)

    if (value === AXIS_TYPE.X) {
      setValue('sort.code', watchBarList.at(0)?.data?.id)
      setWatchedObject(watchBarList.at(0)?.data?.id)
    }

    if (value === AXIS_TYPE.Y) {
      setValue('sort.code', watchedObjectY?.id)
      setWatchedObject(watchedObjectY?.id)
    }
  }

  const handleChangeIsTimeWindow = (value: boolean) => {
    setValue('canvas.axisX.isTimeWindow', value)

    if (!value) {
      setValue('sort.axis', AXIS_TYPE.Y)
      handleChangeSort(AXIS_TYPE.Y)
    }
  }

  return (
    <Box display='flex' flexDirection='column' p={2}>
      <Typography variant='h5'>{t('ganttCreate.mainObjectForm.mainObject')}</Typography>
      <FormInput
        // принято решение использовать только временное окно
        disabled={true}
        inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
        label={t('ganttCreate.generalForm.labels.isTimeWindow')}
        name='canvas.axisX.isTimeWindow'
        value={true}
        formInputContainerSx={{
          width: 'fit-content',
        }}
        sx={{
          marginLeft: '-9px',
        }}
        onChangeCheckbox={handleChangeIsTimeWindow}
      />
      <FormInput
        disabled={!mainObjectOptionsY.length || !mainObjectOptionsX.length || !watchIsTimeWindow}
        inputType={GENERATOR_INPUT_TYPE.RADIO_GROUP}
        label={t('ganttCreate.mainObjectForm.sortAxis')}
        name='sort.axis'
        radioOptionsList={axisOptions}
        formInputContainerSx={{
          width: 'fit-content',
        }}
        onChangeRadio={handleChangeSort}
      />
      {Boolean(watchAxis) && (
        <Box key={watchedSortObjectCode} flex={0.7} mt={2}>
          <FormInput
            disabled={!mainObjectOptions}
            inputType={GENERATOR_INPUT_TYPE.SELECT}
            label={t('ganttCreate.mainObjectForm.sortObj')}
            name='sort.code'
            selectOptions={mainObjectOptions}
            variant='standard'
            onChangeSelect={handleChangeSortObject}
          />
        </Box>
      )}
      <SortFieldArray
        fieldsSelectOptions={fieldsSelectOptions}
        isDisabled={!watchedSortObjectCode}
        isLoading={isLoading}
        watchedObject={watchedSortObjectCode}
      />
    </Box>
  )
}
